import { createMemo, DEV } from "solid-js"
import { isPlainObjDeeplyEqual } from "../../../isPlainObjDeeplyEqual"
import createNodeNeighborAvgElevationListV2 from "../../../three/objects/TerrainMesh1V2/createNodeNeighborAvgElevationListV2"
import eightDirectionCharsLowerCase from "../../direction/eightDirectionCharsLowerCase"
import normalizeCircularOffset from "../../direction/normalizeCircularOffset"
import { ELEVATION_ABSOLUTE_MIN } from "../../entity/EntityElevation/elevation_consts"
import { freeze } from "../../util/Object"
import type { Engine } from "../Engine.type"
import type { TileGraphNode } from "./TileGraphNode.type"

export type NeighborWaterElevationCollection = {
  /** Base elevation of the center tile */
  base: number
  /** Elevations of 8 neighbors in eightDirections order (length === 8) */
  list: number[]  // length === 8
  /** Pre-computed averages for interpolation (length === 8) */
  avgs: number[]  // length === 8
}

const dummyWECList = Array(8).fill(ELEVATION_ABSOLUTE_MIN)
const dummyWEC: NeighborWaterElevationCollection = {
  base: ELEVATION_ABSOLUTE_MIN,
  list: dummyWECList,
  avgs: dummyWECList,
}

if (DEV) {
  freeze(dummyWEC)
}

export default function createTileGraphNodeWaterElevationCollectionMemo(
  engine: Engine,
  node: TileGraphNode,
) {
  // const getNodeNeighborIndexMap = useNodeNeighborIndexMap(engine)

  const memo = createMemo<NeighborWaterElevationCollection>(() => {
    const base = node.water_e()
    // const waterProximityKind = node.water_prox()
    // const nodeNeighborByDirectionMap = getNodeNeighborIndexMap()
    
    // Build neighbor elevation list in eightDirections order: [N, NE, E, SE, S, SW, W, NW]
    const neighborBases: number[] = []

    for (let dirIndex0 = 0; dirIndex0 < 8; dirIndex0++) {
      const dirLc0 = eightDirectionCharsLowerCase[dirIndex0]
      
      // Get neighbor node using the direction property names
      const neighborNode: TileGraphNode | null = node[dirLc0]

      // Handle missing neighbors with fallback strategy
      if (!neighborNode) {
        // For diagonal neighbors (odd indices: NE, SE, SW, NW)
        if (dirIndex0 % 2 === 1) {
          // Try first adjacent cardinal direction (e.g., NE -> N)
          const dirIndex1 = normalizeCircularOffset(dirIndex0 + 1, 8)
          const dirLc1 = eightDirectionCharsLowerCase[dirIndex1]
          const neighborNode1: TileGraphNode | null = node[dirLc1]
          if (neighborNode1) {
            neighborBases[dirIndex0] = neighborNode1.water_e()
            continue
          }

          // Try second adjacent cardinal direction (e.g., NE -> E)
          const dirIndex2 = normalizeCircularOffset(dirIndex0 - 1, 8)
          const dirLc2 = eightDirectionCharsLowerCase[dirIndex2]
          const neighborNode2: TileGraphNode | null = node[dirLc2]
          if (neighborNode2) {
            neighborBases[dirIndex0] = neighborNode2.water_e()
            continue
          }

          // Both adjacent directions missing, use base elevation
          neighborBases[dirIndex0] = base
          continue
        }

        // For cardinal neighbors (even indices: N, E, S, W), use base elevation
        neighborBases[dirIndex0] = base
        continue
      }

      // Neighbor exists, use its elevation
      neighborBases[dirIndex0] = neighborNode.water_e()
    }

    // Pre-compute averaged elevations for smooth interpolation
    const avgs = createNodeNeighborAvgElevationListV2(base, neighborBases)

    const collection: NeighborWaterElevationCollection = {
      base,
      list: neighborBases,
      avgs,
    }
    // if (waterProximityKind !== WaterProximityKind.IsWater) {
    //   // collection.base -= 1
    // }
    return collection
  }, dummyWEC, {
    equals: isPlainObjDeeplyEqual,
  })

  return memo
}